<script setup>
// §8.6 GanttLite (P1) — Project Management milestones. A light timeline: rows
// of bars positioned across a shared date axis, plus milestone diamonds. Bars
// use the ramp (magnitude family, D10) as neutral track fills — NOT identity
// (D9): the label carries which project a bar is, never its colour.
import { computed } from 'vue'
import { RAMP_VARS } from './ramp.js'

const props = defineProps({
	// rows: [{ label, start, end, milestones?: [{ at, label }] }]  values 0..100 (% of axis)
	rows: { type: Array, required: true },
	ticks: { type: Array, default: () => ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] },
})
const color = (i) => RAMP_VARS[i % RAMP_VARS.length]
</script>

<template>
	<div class="l-gantt">
		<div class="axis">
			<div class="axis-label"></div>
			<div class="ticks">
				<span v-for="(t, i) in ticks" :key="i" class="tick">{{ t }}</span>
			</div>
		</div>
		<div v-for="(r, i) in rows" :key="i" class="grow">
			<div class="row-label">{{ r.label }}</div>
			<div class="track">
				<div class="grid" aria-hidden="true">
					<span v-for="(t, j) in ticks" :key="j" class="gridline" />
				</div>
				<div class="bar" :style="{ left: r.start + '%', width: r.end - r.start + '%', background: color(i) }">
					<span class="bar-text num">{{ Math.round(r.end - r.start) }}%</span>
				</div>
				<div v-for="(m, k) in r.milestones || []" :key="k" class="milestone no-tooltip-icon"
					:style="{ left: m.at + '%' }" :title="m.label">
					<span class="diamond" aria-hidden="true"></span>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.l-gantt
{
	display: flex;
	flex-direction: column;
	gap: var(--sp-2);
}

.axis,
.grow
{
	display: grid;
	grid-template-columns: 150px 1fr;
	gap: var(--sp-4);
	align-items: center;
}

.ticks
{
	display: flex;
	justify-content: space-between;
	font-size: var(--text-micro);
	font-weight: 600;
	letter-spacing: 0.6px;
	text-transform: uppercase;
	color: var(--text-3);
	padding-bottom: 6px;
	border-bottom: 1px solid var(--line-strong);
}

.row-label
{
	font-size: var(--text-sm);
	font-weight: 500;
	color: var(--text);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.track
{
	position: relative;
	height: 26px;
}

.grid
{
	position: absolute;
	inset: 0;
	display: flex;
	justify-content: space-between;
}

.gridline
{
	width: 1px;
	background: var(--line-soft);
}

.bar
{
	position: absolute;
	top: 4px;
	height: 18px;
	border-radius: var(--r-pill);
	display: flex;
	align-items: center;
	padding: 0 8px;
	min-width: 24px;
}

.bar-text
{
	font-size: 10px;
	font-weight: 700;
	color: var(--on-emphasis);
}

.milestone
{
	position: absolute;
	top: 0;
	bottom: 0;
	display: flex;
	align-items: center;
	transform: translateX(-50%);
}

.diamond
{
	width: 11px;
	height: 11px;
	background: var(--brand);
	border: 2px solid var(--surface);
	transform: rotate(45deg);
}
</style>
